import { ChangeEvent, Dispatch, SetStateAction, useCallback, useState } from 'react';
import useErrors from '../../../../hooks/useErrors';
import useApiCall from '../../../../hooks/useApiCall';
import { toast } from 'react-toastify';
import isEmailValid from '../../../../utils/isEmailValid';
import internalUsersService from '../../../../services/internalUsersService';

interface UseAddModalI {
  loadInternalUsers: () => Promise<void>;
  setAddInternalUserModalShow: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export default function useAddModal({ loadInternalUsers, setAddInternalUserModalShow, setIsLoading }: UseAddModalI) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  const { apiCall } = useApiCall();

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value);

    if (!isEmailValid(event.target.value)) {
      setError({ field: 'email', message: 'E-mail inválido' });
    } else {
      removeError('email');
    }
  }

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value);

    if (event.target.value.length < 8) {
      setError({ field: 'password', message: 'Senha deve conter ter 8 caracteres!' });
    } else if (event.target.value) {
      removeError('password');
    } if (event.target.value !== passwordConfirmation) {
      setError({ field: 'passwordConfirmation', message: 'Senhas divergentes' });
    } else if (event.target.value === passwordConfirmation) {
      removeError('passwordConfirmation');
    }
  }

  function handlePasswordConfirmationChange(event: ChangeEvent<HTMLInputElement>) {
    setPasswordConfirmation(event.target.value);

    if (event.target.value !== password) {
      setError({ field: 'passwordConfirmation', message: 'Senhas divergentes' });
    } else {
      removeError('passwordConfirmation');
    }
  }

  const addInternalUser = useCallback(async () => {
    await apiCall({
      apiToCall: internalUsersService.createInternalUser,
      onStartLoad: () => { setIsLoading(true); },
      reqBody: JSON.stringify({
        email,
        password,
      }),
      actionAfterResponse: (response) => {
        if (response.success) {
          toast.success('Usuário adicionado com sucesso!');
          setAddInternalUserModalShow(false);
          setEmail('');
          setPassword('');
          setPasswordConfirmation('');
          loadInternalUsers();
          return;
        }
        toast.error('Não foi possível adicionar o usuário. Por favor, tente novamente');
        setIsLoading(false);
      },
      catchMessage: 'Não foi possível adicionar o usuário. Por favor, tente novamente',
      catchAction: () => setIsLoading(false),
    });
  }, [apiCall, email, loadInternalUsers, password, setAddInternalUserModalShow, setIsLoading]);

  const isInternalUserFormValid = errors.length === 0 && !!password && !!passwordConfirmation;

  return {
    addInternalUser,
    email,
    password,
    passwordConfirmation,
    handleEmailChange,
    handlePasswordChange,
    handlePasswordConfirmationChange,
    getErrorMessageByFieldName,
    isInternalUserFormValid,
  };
}
